// @ts-check
// @arch:pure-engine
// greenLedger.js — the PURE edit-vs-green ledger (honest-green, code-quality PR12).
//
// Tracks, per file, {lastEditSeq, lastGreenSeq}. A done verdict is STALE when a file
// was edited AFTER its last verified green — "you changed it after it last passed, so
// the green no longer covers what's on disk." The done gate treats stale as
// !reachedGreen and re-steers.
//
// The clock is a monotonic composite {sessionId, seq}. `seq` ONLY increases. The
// reset-safety is the whole point: on resume, the ledger is re-seeded from the
// PERSISTED high-water `seq`, so an edit that happened before a crash/recovery can
// NEVER masquerade as "fresh" by getting a low seq again (the lie window mtime-based
// clocks open). Never mtime — the clock is logical and persisted.

/**
 * @typedef {{ lastEditSeq:number, lastGreenSeq:number }} FileMark
 * @typedef {{ sessionId:string|null, seq:number, files:Record<string,FileMark> }} GreenLedger
 */

/** Create a ledger, optionally seeded from persisted state (resume / reset-safe). */
export function createLedger(seed = {}) {
  const files = {};
  for (const [path, m] of Object.entries(seed.files || {})) {
    files[path] = { lastEditSeq: m?.lastEditSeq | 0, lastGreenSeq: m?.lastGreenSeq | 0 };
  }
  return {
    sessionId: seed.sessionId ?? null,
    // Seed from the high-water mark so post-resume edits sort AFTER pre-resume greens.
    seq: Number.isFinite(seed.seq) ? Math.max(0, Math.trunc(seed.seq)) : 0,
    files,
  };
}

/** Advance the logical clock and return the new seq. */
export function tick(ledger) {
  ledger.seq = (ledger.seq | 0) + 1;
  return ledger.seq;
}

function mark(ledger, path) {
  let m = ledger.files[path];
  if (!m) { m = { lastEditSeq: 0, lastGreenSeq: 0 }; ledger.files[path] = m; }
  return m;
}

/** Record that `path` was edited (write/apply_edit) — bumps its lastEditSeq. */
export function recordEdit(ledger, path) {
  mark(ledger, path).lastEditSeq = tick(ledger);
  return ledger;
}

/** Record that the listed paths (or all tracked files) reached verified green. */
export function recordGreen(ledger, paths) {
  const seq = tick(ledger);
  const targets = (paths && paths.length) ? paths : Object.keys(ledger.files);
  for (const path of targets) mark(ledger, path).lastGreenSeq = seq;
  return ledger;
}

/** A file is stale when it was edited after its last green (and it HAD a green). */
export function fileStale(m) {
  return !!m && m.lastGreenSeq > 0 && m.lastEditSeq > m.lastGreenSeq;
}

/** The verdict is stale when ANY tracked file is stale. */
export function isStale(ledger) {
  if (!ledger || !ledger.files) return false;
  return Object.values(ledger.files).some(fileStale);
}

/** The names of the files that are stale (for the re-steer message). */
export function staleFiles(ledger) {
  if (!ledger || !ledger.files) return [];
  return Object.entries(ledger.files).filter(([, m]) => fileStale(m)).map(([path]) => path);
}

/** Serialize the persistable state (the high-water seq + per-file marks). */
export function snapshotLedger(ledger) {
  return { sessionId: ledger.sessionId, seq: ledger.seq, files: { ...ledger.files } };
}
